import { ActivationFormState, ActivationStep } from '../types';

const DRAFT_VERSION = 1;

export type ActivationDraftScope =
  | { type: 'estate'; estateId: string }
  | { type: 'collaborator-create-latest' }
  | { type: 'collaborator-create'; estateId: string }
  | { type: 'collaborator-edit'; collaboratorId: string };

export type ActivationDraft<T extends ActivationFormState = ActivationFormState> = {
  version: number;
  updatedAt: string;
  step: ActivationStep;
  form: T;
};

const buildStorageKey = (scope: ActivationDraftScope): string => {
  switch (scope.type) {
    case 'estate':
      return `standalone-charges:activation-draft:estate:${scope.estateId}`;
    case 'collaborator-create-latest':
      return 'standalone-charges:activation-draft:collaborator:create:latest';
    case 'collaborator-create':
      return `standalone-charges:activation-draft:collaborator:create:${scope.estateId}`;
    case 'collaborator-edit':
      return `standalone-charges:activation-draft:collaborator:edit:${scope.collaboratorId}`;
    default:
      return 'standalone-charges:activation-draft:unknown';
  }
};

const canUseStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const readActivationDraft = <T extends ActivationFormState = ActivationFormState>(
  scope: ActivationDraftScope
): ActivationDraft<T> | null => {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(buildStorageKey(scope));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as ActivationDraft<T>;
    if (!parsed || parsed.version !== DRAFT_VERSION || !parsed.form) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const writeActivationDraft = <T extends ActivationFormState = ActivationFormState>(
  scope: ActivationDraftScope,
  draft: Pick<ActivationDraft<T>, 'step' | 'form'>
): void => {
  if (!canUseStorage()) return;

  try {
    const payload: ActivationDraft<T> = {
      version: DRAFT_VERSION,
      updatedAt: new Date().toISOString(),
      step: draft.step,
      form: draft.form
    };
    window.localStorage.setItem(buildStorageKey(scope), JSON.stringify(payload));
  } catch {
    // Ignore quota or serialization errors.
  }
};

export const clearActivationDraft = (scope: ActivationDraftScope): void => {
  if (!canUseStorage()) return;

  try {
    window.localStorage.removeItem(buildStorageKey(scope));
  } catch {
    // Ignore storage errors.
  }
};

export const hasActivationDraft = (scope: ActivationDraftScope): boolean =>
  Boolean(readActivationDraft(scope));
